// theme.js — RedSpeed brand tokens for GapGo's guest-facing pages.
// Values are copied 1:1 from redspeed-website/app/globals.css (dark theme block)
// so GapGo's guest flow matches the real RedSpeed site, not an approximation.
// Internal tools (Kitchen/Admin) are out of scope — see REDSPEED.md §10.

export const FONT = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

export const COLOR = {
  bg: '#000000',
  bgElev: '#0b0b0b',
  text: '#F5F5F7',
  textMuted: 'rgba(245, 245, 247, 0.60)',
  textFaint: 'rgba(245, 245, 247, 0.40)',

  accent: '#E8001D',
  accentDeep: '#B00016',

  glassBg: 'rgba(255, 255, 255, 0.04)',
  glassBgHeavy: 'rgba(15, 15, 16, 0.72)',
  glassBorder: 'rgba(255, 255, 255, 0.10)',
  glassSpecular: 'rgba(255, 255, 255, 0.10)',

  chipBg: 'rgba(255, 255, 255, 0.06)',
  chipBorder: 'rgba(255, 255, 255, 0.10)',
  chipText: 'rgba(245, 245, 247, 0.65)',

  hairline: 'rgba(255, 255, 255, 0.08)',
  shadowSoft: 'rgba(0, 0, 0, 0.50)',
  shadowDeep: 'rgba(0, 0, 0, 0.70)',
};

export const RADIUS = { glass: '24px', pill: '9999px' };

// .glass — the signature RedSpeed surface.
export const glass = (overrides = {}) => ({
  background: COLOR.glassBg,
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  border: `1px solid ${COLOR.glassBorder}`,
  borderRadius: RADIUS.glass,
  boxShadow: `inset 0 1px 0 ${COLOR.glassSpecular}, 0 8px 40px ${COLOR.shadowSoft}`,
  ...overrides,
});

export const glassHeavy = (overrides = {}) => ({
  background: COLOR.glassBgHeavy,
  backdropFilter: 'blur(28px)',
  WebkitBackdropFilter: 'blur(28px)',
  border: `1px solid ${COLOR.glassBorder}`,
  borderRadius: RADIUS.glass,
  boxShadow: `inset 0 1px 0 ${COLOR.glassSpecular}, 0 24px 80px ${COLOR.shadowDeep}`,
  ...overrides,
});

// .glass-red — red-tinted glass with a soft glow, for featured/CTA surfaces.
export const glassRed = (overrides = {}) => ({
  background: 'rgba(232, 0, 29, 0.06)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  border: '1px solid rgba(232, 0, 29, 0.40)',
  borderRadius: RADIUS.glass,
  boxShadow: `inset 0 1px 0 ${COLOR.glassSpecular}, 0 0 60px rgba(232, 0, 29, 0.18), 0 8px 40px ${COLOR.shadowSoft}`,
  ...overrides,
});

// .btn-red — primary pill CTA.
export const btnRed = (overrides = {}) => ({
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  height: '48px', padding: '0 28px', borderRadius: RADIUS.pill,
  background: COLOR.accent, color: '#fff',
  fontSize: '15px', fontWeight: '600', letterSpacing: '-0.01em',
  border: 'none', cursor: 'pointer', fontFamily: FONT,
  minWidth: '44px', minHeight: '44px',
  ...overrides,
});

// .btn-ghost — bordered pill.
export const btnGhost = (overrides = {}) => ({
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  height: '48px', padding: '0 28px', borderRadius: RADIUS.pill,
  background: 'transparent', color: COLOR.text,
  fontSize: '15px', fontWeight: '600', letterSpacing: '-0.01em',
  border: `1px solid ${COLOR.glassBorder}`, cursor: 'pointer', fontFamily: FONT,
  minWidth: '44px', minHeight: '44px',
  ...overrides,
});

// .chip — small pill label.
export const chip = (overrides = {}) => ({
  display: 'inline-flex', alignItems: 'center', padding: '4px 12px',
  borderRadius: RADIUS.pill, background: COLOR.chipBg, border: `1px solid ${COLOR.chipBorder}`,
  fontSize: '11px', fontWeight: '500', color: COLOR.chipText, letterSpacing: '0.01em',
  whiteSpace: 'nowrap', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
  ...overrides,
});
